import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Divider,
  Alert
} from '@heroui/react';
import { PencilIcon, PhotoIcon } from '@heroicons/react/24/outline';
import CompanyConfigForm from '@/components/empresa/CompanyConfigForm';
import { getCompanyInfo } from '@/services/empresa/company.service';
import type { CompanyData } from '@/types/company.model';
import ComposeImg from '../shared/composeImg';

const CompanyConfigurationTab: React.FC = () => {
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving,] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success,] = useState<string | null>(null);
  // Use a ref to ensure the auth flow runs only once even with StrictMode
  const hasExecutedRef = useRef(false);

  // Fetch company data on component mount
  useEffect(() => {
    if (hasExecutedRef.current) return; // Prevent execution in StrictMode's second call
    hasExecutedRef.current = true;
    const fetchCompanyData = async () => {
      try {
        setIsLoading(true);
        const data = await getCompanyInfo();
        setCompanyData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching company info:', err);
        setError('No se pudo cargar la información de la empresa. Por favor, inténtelo de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanyData();
  }, []);


  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Cargando información de la empresa...</div>
      </div>
    );
  }

  if (error && !companyData) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <Alert color="danger" title="Error">
          {error}
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto p-4">
      <Card className="w-full">
        <CardHeader className="flex flex-row justify-between items-center">
          <h2 className="text-2xl font-bold">Configuración de Empresa</h2>
        </CardHeader>
        <Divider />
        <CardBody className="py-4">
          {error && (
            <Alert color="danger" className="mb-4">
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert color="success" className="mb-4">
              {success}
            </Alert>
          )}
          
          {isEditing ? (
            companyData && (
              <CompanyConfigForm
                companyData={companyData}
                onCancel={() => setIsEditing(false)}
                isLoading={isSaving}
              />
            )
          ) : (
            companyData && (
              <div className="space-y-6">
                <div className="mb-6 flex justify-center">
                  {companyData.logo && typeof companyData.logo === 'object' ? (
                    <ComposeImg thumb={companyData.logo.url} full={ companyData.logo.url} className='w-32 h-32 object-contain rounded-lg'></ComposeImg>
                  ) : (
                    <div className="bg-gray-100 border-2 border-dashed rounded-xl w-32 h-32 flex items-center justify-center">
                      <PhotoIcon className="w-12 h-12 text-gray-400" />
                      <span className="sr-only">Logo de empresa</span>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Nombre de la Empresa</p>
                    <p className="font-medium">{companyData.nombre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Razón Social</p>
                    <p className="font-medium">{companyData.razonSocial}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tipo de Identificación</p>
                    <p className="font-medium">{companyData.tipoIdentificacion}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Número de Identificación</p>
                    <p className="font-medium">{companyData.numeroIdentificacion}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Dirección</p>
                    <p className="font-medium">{companyData.direccion || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Teléfono</p>
                    <p className="font-medium">{companyData.telefono}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{companyData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email de Contacto</p>
                    <p className="font-medium">{companyData.emailContacto || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Persona de Contacto</p>
                    <p className="font-medium">{companyData.personaContacto || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tamaño de la Empresa</p>
                    <p className="font-medium">{companyData.tamano}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Industria</p>
                    <p className="font-medium">{companyData.industria}</p>
                  </div>
                </div>
              </div>
            )
          )}
        </CardBody>
        {!isEditing && (
          <CardFooter className="flex justify-end">
            <Button 
              color="primary" 
              startContent={<PencilIcon className="w-4 h-4" />}
              onClick={() => setIsEditing(true)}
            >
              Editar Información
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export default CompanyConfigurationTab;